import { Injectable } from '@angular/core';
import {
  IClientSignField,
  ClientSignFieldsByDocument,
} from '@app/transaction-builder/model/sign-field.model';
import { ITransactionData } from '@app/transaction-builder/model/transaction-data.model';
import { SignFieldsStorageService } from '@app/transaction-builder/services/sign-fields-storage.service';
import { StorageService } from 'ngx-mat-esign360';
import { ITransactionFormData } from '@app/transaction-builder/model/transaction-form-data.model';
import { SignatureType } from 'ngx-mat-esign360';
import { EmptyTransactionDataFactoryService } from './empty-transaction-data-factory.service';

@Injectable({
  providedIn: 'root',
})
export class TransactionBuilderStorageService {
  readonly TRANSACTION_FORM_DATA_KEY = 'transactionFormData';
  readonly AUTO_INCREMENT_CLIENT_ID_KEY = 'autoIncrementClientId';
  readonly TRANSACTION_DOCUMENT_DATA_KEY = 'transactionDocumentData';
  readonly CAPTURED_TRANSACTION_DEF_KEY = 'capturedTransactionDefinition';
  readonly CAPTURED_TRANSACTION_DATA_KEY = 'capturedTransactionData';
  readonly SIGN_FIELDS_KEY = 'signFieldsByDocument';
  readonly DEFAULT_SIGNATURE_TYPE_KEY = 'defaultSignatureType';

  constructor(
    private storage: StorageService,
    private signFieldsStorageService: SignFieldsStorageService,
    private emptyTransactionDataFactoryService: EmptyTransactionDataFactoryService
  ) {
    if (!this.getSignFieldsByDocument()) {
      this.setSignFieldsByDocument({});
    }
  }

  getTransactionData(): ITransactionData {
    return {
      formData:
        this.storage.getData(this.TRANSACTION_FORM_DATA_KEY) ||
        this.emptyTransactionDataFactoryService.createEmptyTransactionFormData(),
      autoIncrementClientId: this.storage.getData(this.AUTO_INCREMENT_CLIENT_ID_KEY) || false,
      documentsData: this.storage.getData(this.TRANSACTION_DOCUMENT_DATA_KEY) || [],
      signFieldsByDocument: this.getSignFieldsByDocument(),
      defaultSignatureType: this.getDefaultSignatureType(),
    };
  }

  setTransactionData(transactionData: ITransactionData) {
    this.setTransactionFormData(transactionData.formData);
    this.storage.saveData(this.AUTO_INCREMENT_CLIENT_ID_KEY, transactionData.autoIncrementClientId);
    this.storage.saveData(this.TRANSACTION_DOCUMENT_DATA_KEY, transactionData.documentsData);
    this.setSignFieldsByDocument(transactionData.signFieldsByDocument);
    this.setDefaultSignatureType(transactionData.defaultSignatureType);
  }

  clearTransactionData() {
    this.storage.clearData(this.TRANSACTION_FORM_DATA_KEY);
    this.storage.clearData(this.TRANSACTION_DOCUMENT_DATA_KEY);
  }

  setTransactionFormData(formData: ITransactionFormData) {
    this.storage.saveData(this.TRANSACTION_FORM_DATA_KEY, formData);
  }

  getCapturedTransactionData(): ITransactionData {
    return (
      this.storage.getData(this.CAPTURED_TRANSACTION_DATA_KEY) ||
      this.emptyTransactionDataFactoryService.createEmptyTransactionData()
    );
  }

  setCapturedTransactionData(transactionData: ITransactionData) {
    this.storage.saveData(this.CAPTURED_TRANSACTION_DATA_KEY, transactionData);
  }

  getSignFieldsByDocument(): ClientSignFieldsByDocument {
    return this.signFieldsStorageService.getSignFieldsByDocument();
  }

  setSignFieldsByDocument(signFieldsByDocuments: ClientSignFieldsByDocument) {
    this.signFieldsStorageService.setSignFieldsByDocument(signFieldsByDocuments);
  }

  clearSignFieldsByDocument() {
    this.signFieldsStorageService.clearSignFieldsByDocument();
  }

  getDocumentSignFields(documentCid: string): IClientSignField[] {
    return this.signFieldsStorageService.getDocumentSignFields(documentCid);
  }

  setDocumentSignFields(documentCid: string, signFields: IClientSignField[]) {
    this.signFieldsStorageService.setDocumentSignFields(documentCid, signFields);
  }

  clearDocumentSignFields(documentCid: string) {
    this.signFieldsStorageService.clearDocumentSignFields(documentCid);
  }

  getDefaultSignatureType(): SignatureType {
    return this.storage.getData(this.DEFAULT_SIGNATURE_TYPE_KEY) || SignatureType.Hand;
  }

  setDefaultSignatureType(type: SignatureType) {
    this.storage.saveData(this.DEFAULT_SIGNATURE_TYPE_KEY, type);
  }

  clearDefaultSignatureType() {
    this.storage.clearData(this.DEFAULT_SIGNATURE_TYPE_KEY);
  }

  clearAllTransactionData() {
    this.clearTransactionData();
    this.clearSignFieldsByDocument();
    this.clearDefaultSignatureType();
  }
}
